import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import AlertDialog from "./AlertDialog";
import UserList from "./UserList";
import PluginsManagement from "./PluginsManagement";
import CompanyProfileManagement from "./CompanyProfileManagement";
import BuildiumUserList from "./BuildiumUserList";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [stagedUsers, setStagedUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [unions, setUnions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("crew");
  const [isQuickBooksConnected, setIsQuickBooksConnected] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
    showCopyButton: false,
  });
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [currentUserPage, setCurrentUserPage] = useState(1);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchUsers();
    if (location.pathname.includes("/quickbooks-success")) {
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "QuickBooks connected successfully!",
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          setIsQuickBooksConnected(true);
          navigate("/user-management", { replace: true });
        },
        showCopyButton: false,
      });
    } else if (location.pathname.includes("/quickbooks-error")) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to connect QuickBooks. Please try again.",
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          navigate("/user-management", { replace: true });
        },
        showCopyButton: false,
      });
    }
  }, [location.pathname]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(
        "https://rentdoc-a0d223e1499e.herokuapp.com/api/users",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const sortedUsers = response.data.sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      );
      setUsers(sortedUsers);
      setStagedUsers(sortedUsers);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching users:", err);
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to load users. Please try again.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        showCopyButton: false,
      });
      setLoading(false);
    }
  };

  const tabs = [
    { key: "crew", label: "User Management" },
    { key: "plugins", label: "Plugins" },
    { key: "buildium", label: "Buildium Users" },
  ];

  if (loading)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500 text-lg">Loading...</div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <AlertDialog
          type={dialog.type}
          title={dialog.title}
          message={dialog.message}
          confirmText="OK"
          cancelText="Close"
          open={dialog.open}
          onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
          onConfirm={dialog.onConfirm}
          showCopyButton={dialog.showCopyButton}
          onCopy={dialog.onCopy}
        />

        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-gray-900">Settings</h1>
          <p className="text-gray-500 mt-1">
            Manage users, plugins, and integrations
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex gap-2 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setCurrentUserPage(1);
              }}
              className={`px-5 py-2.5 rounded-2xl text-sm font-semibold transition-all ${
                activeTab === tab.key
                  ? "bg-indigo-600 text-white shadow-md"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-indigo-300 hover:text-indigo-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "crew" && (
          <UserList
            users={users}
            setUsers={setUsers}
            stagedUsers={stagedUsers}
            setStagedUsers={setStagedUsers}
            accounts={accounts}
            unions={unions}
            setDialog={setDialog}
            navigate={navigate}
            isAddingUser={isAddingUser}
            setIsAddingUser={setIsAddingUser}
            currentUserPage={currentUserPage}
            setCurrentUserPage={setCurrentUserPage}
          />
        )}
        {activeTab === "buildium" && <BuildiumUserList setDialog={setDialog} />}
        {activeTab === "plugins" && (
          <PluginsManagement
            isQuickBooksConnected={isQuickBooksConnected}
            setDialog={setDialog}
          />
        )}

        <CompanyProfileManagement />
      </div>
    </div>
  );
}
